import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function PromptSettings() {
  const { user, getAuthHeaders } = useAuth();
  const [promptSets, setPromptSets] = useState([]);
  const [promptSchema, setPromptSchema] = useState(null);

  if (user && !user.is_admin && !user.can_create_prompts) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-600">You do not have permission to manage custom prompts.</p>
      </div>
    );
  }
  const [loading, setLoading] = useState(true);
  const [editingSet, setEditingSet] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrompts, setEditPrompts] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  const fetchSets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/user/prompt-sets/`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setPromptSets(data.prompt_sets || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchSchema = useCallback(async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/user/prompt-sets/schema`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load schema');
      setPromptSchema(data.prompts);
    } catch (err) { setError(err.message); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSets(); fetchSchema(); }, [fetchSets, fetchSchema]);

  const startCreate = () => {
    setEditingSet('new'); setEditName(''); setEditPrompts({}); setExpanded({}); setError(null);
  };

  const startEdit = async (ps) => {
    setError(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/user/prompt-sets/${ps.id}`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setEditingSet(data.prompt_set);
      setEditName(data.prompt_set.name);
      setEditPrompts(data.prompt_set.prompts || {});
      setExpanded({});
    } catch (err) { setError(err.message); }
  };

  const handleSave = async () => {
    if (!editName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const isNew = editingSet === 'new';
      const url = isNew
        ? `${config.API_BASE_URL}/user/prompt-sets/`
        : `${config.API_BASE_URL}/user/prompt-sets/${editingSet.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim(), prompts: editPrompts }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setEditingSet(null); fetchSets();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prompt set?')) return;
    setDeletingId(id); setError(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/user/prompt-sets/${id}`, {
        method: 'DELETE', headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      fetchSets();
    } catch (err) { setError(err.message); }
    finally { setDeletingId(null); }
  };

  const resetPrompt = (key) => {
    setEditPrompts(prev => { const next = { ...prev }; delete next[key]; return next; });
  };

  const modifiedCount = Object.keys(editPrompts).length;

  // ── Edit / Create view ──
  if (editingSet) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-kaleido-gray-700">
            {editingSet === 'new' ? 'New Prompt Set' : 'Edit Prompt Set'}
          </h3>
          {modifiedCount > 0 && (
            <span className="text-[10px] text-kaleido-purple bg-kaleido-purple/10 px-1.5 py-0.5 rounded font-medium">
              {modifiedCount} modified
            </span>
          )}
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">{error}</div>
        )}

        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          placeholder="Set name"
          className="w-full px-2.5 py-1.5 border border-kaleido-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-kaleido-purple/30 focus:border-kaleido-purple"
        />

        {/* Prompt accordions */}
        <div className="space-y-1">
          {promptSchema && Object.entries(promptSchema).map(([key, meta]) => (
            <div key={key} className="border border-kaleido-gray-200 rounded-md overflow-hidden">
              <button
                onClick={() => setExpanded(p => ({ ...p, [key]: !p[key] }))}
                className="w-full flex items-center justify-between px-2.5 py-2 bg-gray-50/80 hover:bg-gray-100/80 transition-colors text-left"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[11px] font-medium text-kaleido-gray-700 truncate">{meta.name}</span>
                  {editPrompts[key] && (
                    <span className="w-1.5 h-1.5 rounded-full bg-kaleido-purple shrink-0"/>
                  )}
                </div>
                <svg className={`w-3 h-3 text-kaleido-gray-400 transition-transform shrink-0 ${expanded[key] ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expanded[key] && (
                <div className="px-2.5 py-2 space-y-1.5 border-t border-kaleido-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-0.5">
                      {meta.variables.map(v => (
                        <span key={v} className="text-[9px] font-mono bg-kaleido-purple/8 text-kaleido-purple/80 px-1 py-0.5 rounded">{v}</span>
                      ))}
                      {meta.variables.length === 0 && <span className="text-[9px] text-kaleido-gray-400 italic">No variables</span>}
                    </div>
                    <button onClick={() => resetPrompt(key)}
                      className="text-[10px] text-kaleido-gray-400 hover:text-kaleido-purple transition-colors">
                      Reset
                    </button>
                  </div>
                  <p className="text-[10px] text-kaleido-gray-400">{meta.description}</p>
                  <textarea
                    value={editPrompts[key] ?? meta.default_text}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === meta.default_text) resetPrompt(key);
                      else setEditPrompts(prev => ({ ...prev, [key]: val }));
                    }}
                    rows={6}
                    className="w-full px-2 py-1.5 border border-kaleido-gray-200 rounded text-[11px] font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-kaleido-purple/30 focus:border-kaleido-purple resize-y"
                  />
                  <p className="text-[9px] text-kaleido-gray-300">Node: {meta.node}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-1.5 pt-1">
          <button onClick={handleSave} disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-kaleido-purple hover:bg-kaleido-purple/90 disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : editingSet === 'new' ? 'Create' : 'Save'}
          </button>
          <button onClick={() => setEditingSet(null)} disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-kaleido-gray-500 border border-kaleido-gray-200 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div className="space-y-3">
      <p className="text-xs text-kaleido-gray-500">
        Create custom prompt configurations that appear in the chat toolbar.
      </p>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-1.5 text-xs text-kaleido-gray-400">
          <div className="w-3 h-3 border-2 border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin"/>
          Loading...
        </div>
      ) : promptSets.length === 0 ? (
        <p className="text-xs text-kaleido-gray-400 italic">No custom prompt sets yet.</p>
      ) : (
        <div className="space-y-1">
          {promptSets.map(ps => (
            <div key={ps.id} className="flex items-center justify-between px-2.5 py-2 rounded-md border border-kaleido-gray-200 hover:border-kaleido-purple/15 transition-colors">
              <div className="min-w-0">
                <div className="text-xs font-medium text-kaleido-gray-700 truncate">{ps.name}</div>
                {ps.updated_at && (
                  <div className="text-[10px] text-kaleido-gray-400">
                    {new Date(ps.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => startEdit(ps)}
                  className="px-2 py-1 text-[10px] font-medium text-kaleido-purple border border-kaleido-purple/15 rounded hover:bg-kaleido-purple/5 transition-colors">
                  Edit
                </button>
                <button onClick={() => handleDelete(ps.id)} disabled={deletingId === ps.id}
                  className="px-2 py-1 text-[10px] font-medium text-red-500 border border-red-200 rounded hover:bg-red-50 transition-colors disabled:opacity-50">
                  {deletingId === ps.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={startCreate} disabled={promptSets.length >= 10}
        className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-kaleido-purple hover:bg-kaleido-purple/90 disabled:opacity-50 transition-colors">
        New Prompt Set
      </button>
      {promptSets.length >= 10 && (
        <p className="text-[10px] text-kaleido-gray-400">Limit: 10 sets</p>
      )}
    </div>
  );
}

export default PromptSettings;
